/**
 * Sistema de logging seguro para la aplicación
 * No expone información sensible en producción
 */

const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
};

const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

/**
 * Logger seguro que no expone datos sensibles en producción
 */
class SecureLogger {
  constructor() {
    this.logLevel = isDevelopment ? LOG_LEVELS.DEBUG : LOG_LEVELS.ERROR;
  }

  /**
   * Sanitiza datos para logging (elimina información sensible)
   * @param {any} data - Datos a sanitizar
   * @returns {any} - Datos sanitizados
   */
  sanitizeForLogging(data) {
    if (!data) return data;
    
    if (typeof data === 'string') {
      // Eliminar tokens, contraseñas, emails, etc.
      return data
        .replace(/token["\s]*[:=]["\s]*[^"'\s,}]+/gi, 'token: [REDACTED]')
        .replace(/password["\s]*[:=]["\s]*[^"'\s,}]+/gi, 'password: [REDACTED]')
        .replace(/email["\s]*[:=]["\s]*[^"'\s,}]+/gi, 'email: [REDACTED]')
        .replace(/authorization["\s]*[:=]["\s]*[^"'\s,}]+/gi, 'authorization: [REDACTED]')
        .replace(/bearer["\s]*[:=]["\s]*[^"'\s,}]+/gi, 'bearer: [REDACTED]');
    }
    
    if (Array.isArray(data)) {
      return data.map(item => this.sanitizeForLogging(item));
    }
    
    if (data && typeof data === 'object') {
      const sanitized = {};
      for (const [key, value] of Object.entries(data)) {
        // Redactar campos sensibles
        if (['password', 'token', 'authorization', 'email', 'secret', 'key'].includes(key.toLowerCase())) {
          sanitized[key] = '[REDACTED]';
        } else {
          sanitized[key] = this.sanitizeForLogging(value);
        }
      }
      return sanitized;
    }
    
    return data;
  }

  /**
   * Log de error (siempre visible)
   * @param {string} message - Mensaje de error
   * @param {any} data - Datos adicionales (opcional)
   */
  error(message, data = null) {
    if (this.logLevel >= LOG_LEVELS.ERROR) {
      const sanitizedData = data ? this.sanitizeForLogging(data) : null;
      if (sanitizedData !== null) {
        console.error(`❌ [ERROR] ${message}`, sanitizedData);
      } else {
        console.error(`❌ [ERROR] ${message}`);
      }
    }
  }

  /**
   * Log de advertencia
   * @param {string} message - Mensaje de advertencia
   * @param {any} data - Datos adicionales (opcional)
   */
  warn(message, data = null) {
    if (this.logLevel >= LOG_LEVELS.WARN) {
      const sanitizedData = data ? this.sanitizeForLogging(data) : null;
      if (sanitizedData !== null) {
        console.warn(`⚠️  [WARN] ${message}`, sanitizedData);
      } else {
        console.warn(`⚠️  [WARN] ${message}`);
      }
    }
  }

  /**
   * Log de información (solo en desarrollo)
   * @param {string} message - Mensaje informativo
   * @param {any} data - Datos adicionales (opcional)
   */
  info(message, data = null) {
    if (this.logLevel >= LOG_LEVELS.INFO && isDevelopment) {
      const sanitizedData = data ? this.sanitizeForLogging(data) : null;
      if (sanitizedData !== null) {
        console.info(`ℹ️  [INFO] ${message}`, sanitizedData);
      } else {
        console.info(`ℹ️  [INFO] ${message}`);
      }
    }
  }

  /**
   * Log de debug (solo en desarrollo)
   * @param {string} message - Mensaje de debug
   * @param {any} data - Datos adicionales (opcional)
   */
  debug(message, data = null) {
    if (this.logLevel >= LOG_LEVELS.DEBUG && isDevelopment) {
      const sanitizedData = data ? this.sanitizeForLogging(data) : null;
      if (sanitizedData !== null) {
        console.log(`🔍 [DEBUG] ${message}`, sanitizedData);
      } else {
        console.log(`🔍 [DEBUG] ${message}`);
      }
    }
  }

  /**
   * Log de datos de usuario (nunca en producción)
   * @param {string} message - Mensaje
   * @param {any} data - Datos del usuario
   */
  userData(message, data = null) {
    if (isDevelopment) {
      const sanitizedData = data ? this.sanitizeForLogging(data) : null;
      if (sanitizedData !== null) {
        console.log(`👤 [USER_DATA] ${message}`, sanitizedData);
      } else {
        console.log(`👤 [USER_DATA] ${message}`);
      }
    }
  }

  /**
   * Log de API (sin datos sensibles)
   * @param {string} method - Método HTTP
   * @param {string} url - URL de la API
   * @param {number} status - Código de estado
   * @param {any} data - Datos de respuesta (opcional)
   */
  api(method, url, status, data = null) {
    if (isDevelopment) {
      const sanitizedData = data ? this.sanitizeForLogging(data) : null;
      const icon = status >= 200 && status < 300 ? '✅' : '❌';
      if (sanitizedData !== null) {
        console.log(`${icon} [API] ${method} ${url} - ${status}`, sanitizedData);
      } else {
        console.log(`${icon} [API] ${method} ${url} - ${status}`);
      }
    }
  }
}

// Instancia singleton del logger
const logger = new SecureLogger();

// Función para reemplazar console.log en producción
const secureConsole = {
  log: (message, ...args) => {
    if (isDevelopment) {
      logger.debug(message, ...args);
    }
  },
  error: (message, ...args) => {
    logger.error(message, args.length > 0 ? args[0] : null);
  },
  warn: (message, ...args) => {
    logger.warn(message, args.length > 0 ? args[0] : null);
  },
  info: (message, ...args) => {
    logger.info(message, args.length > 0 ? args[0] : null);
  }
};

export default logger;
export { secureConsole };